import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { adminActions } from '../actions/admin.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class AdminEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getAdmin$ = createEffect(() => {
        return this.actions.pipe(
            ofType(adminActions.getAdmin),
            mergeMap(({ id }) => {
                return this.httpService.getAdmin(id).pipe(
                    finalizeLoader('admin-get'),
                    map((admin) => adminActions.getAdminSuccess({ payload: admin })),
                    catchError((response: HttpErrorResponse) => of(adminActions.getAdminError({ code: response.status }))),
                );
            }),
        );
    });

    createAdmin$ = createEffect(() => {
        return this.actions.pipe(
            ofType(adminActions.createAdmin),
            mergeMap(({ value }) => {
                return this.httpService.createAdmin(value).pipe(
                    finalizeLoader('admin-create'),
                    map((admin) => adminActions.createAdminSuccess({ payload: admin })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.admins.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.admins.createError');
                        return of(adminActions.createAdminError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateAdmin$ = createEffect(() => {
        return this.actions.pipe(
            ofType(adminActions.updateAdmin),
            mergeMap(({ id, value }) => {
                return this.httpService.updateAdmin(id, value).pipe(
                    finalizeLoader('admin-update'),
                    map((admin) => adminActions.updateAdminSuccess({ payload: admin })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.admins.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.admins.updateError');
                        return of(adminActions.updateAdminError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteAdmin$ = createEffect(() => {
        return this.actions.pipe(
            ofType(adminActions.deleteAdmin),
            mergeMap(({ id }) => {
                return this.httpService.deleteAdmin(id).pipe(
                    finalizeLoader('admin-delete'),
                    map(() => adminActions.deleteAdminSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.admins.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.admins.deleteError');
                        return of(adminActions.deleteAdminError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
